import { Warning } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BomPartType } from 'bom/enums';
import { useBomComponentActions } from 'bom/hooks';
import { Confirmation } from 'core/components';
import { useSelectionCountContext } from 'core/states';
import { IJobPart } from 'jobs/types';
import { PartNumberHelper } from 'parts/helpers';
import { Color } from 'theme/enums';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: IJobPart[];
  onSubmitSuccess: () => void;
}

export const RemoveConfirmation = ({ isOpen, onClose, selectedRows, onSubmitSuccess }: IProps) => {
  const { t } = useTranslation();

  const { selectionCount } = useSelectionCountContext();

  const { removeBomComponents } = useBomComponentActions({ selectionCount });

  const confirmationMessage = useMemo(() => {
    const selectedParts = selectedRows.filter((row) => row.type === BomPartType.PART);
    const selectedComponents = selectedRows.filter((row) => row.type === BomPartType.COMPONENT);

    const selectedPartsCount = selectedParts.length;
    const selectedComponentsCount = selectedComponents.length;

    if (selectedPartsCount > 0 && selectedComponentsCount === 0) {
      return t('messageConfirmRemoveJobParts', {
        count: selectedPartsCount,
        number: PartNumberHelper.toLabel(selectedParts[0].partArr?.number || ''),
      });
    }

    if (selectedComponentsCount > 0 && selectedPartsCount === 0) {
      return t('messageConfirmRemoveJobComponents', {
        count: selectedComponentsCount,
        name: selectedComponents[0].componentName,
      });
    }

    return t('messageConfirmRemoveJobPartsAndComponents', {
      partsText: t('messageConfirmRemoveJobPartsAndComponentsPart', {
        count: selectedPartsCount,
      }),
      componentsText: t('messageConfirmRemoveJobPartsAndComponentsComponent', {
        count: selectedComponentsCount,
      }),
    });
  }, [selectedRows, t]);

  const onConfirm = async () => {
    onClose();

    const ids = selectedRows.map((row) => row.componentId).filter(Boolean);

    const { success } = await removeBomComponents(ids);

    if (!success) {
      return;
    }

    onSubmitSuccess();
  };

  return (
    <Confirmation
      isOpen={isOpen}
      title={t('titleRemoveLines', { count: selectedRows.length })}
      icon={<Warning weight="fill" />}
      message={confirmationMessage}
      confirmBtnLabel={t('buttonLabelRemove')}
      confirmBtnColor={Color.DANGER}
      cancelBtnLabel={t('buttonLabelCancel')}
      onConfirm={onConfirm}
      onCancel={onClose}
    />
  );
};
